import { useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, Maximize2, Quote, Search } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { JournalAttachments } from '@/components/journal/JournalAttachments';
import { JournalCorrectionPanel } from '@/components/journal/JournalCorrectionPanel';
import { JournalFormattedView } from '@/components/journal/JournalFormattedView';
import { JournalHistoryResizeHandle } from '@/components/journal/JournalHistoryResizeHandle';
import type { JournalEntryWithAuthor } from '@/hooks/useJournal';
import type { JournalWriteHistoryScope } from '@/lib/journalWritePath';
import {
  JournalHistoryModeTabs,
  type JournalHistoryPanel,
} from '@/components/journal/JournalHistoryModeTabs';
import { JournalWritePatientFiles } from '@/components/journal/JournalWritePatientFiles';
import { JournalWritePatientMarkings } from '@/components/journal/JournalWritePatientMarkings';
import { journalNotePreview } from '@/lib/journalWritePath';
import { filterJournalWriteHistoryEntries } from '@/lib/journalWriteHistoryList';
import { journalQuoteHtml, selectedTextInNode } from '@/lib/journalQuote';
import { JOURNAL_HISTORY_WIDTH_MIN } from '@/lib/journalWritePrefs';
import {
  journalDisplayAppointment,
  journalEntryStatus,
  journalKindLabel,
  type JournalLinkedAppointment,
} from '@/utils/journalHistoryFilters';
import { formatCourseDateTime } from '@/lib/courseDateFormat';
import { JOURNAL_WRITE_ALL_EMPLOYEES } from '@/lib/journalWritePractitionerFilter';

const ICON = 1.75;

export function JournalWriteHistory({
  clinicId,
  patientId,
  entries,
  appointmentById,
  courseAppointmentIds,
  courseName,
  scope,
  onScopeChange,
  practitionerId,
  onPractitionerChange,
  practitioners,
  practitionerMemberIds,
  activeMemberIds,
  search,
  onSearchChange,
  onCite,
  onCollapse,
  onReadFull,
  resizable = false,
  width,
  widthMax,
  onWidthChange,
  onWidthReset,
  userId,
}: {
  clinicId: string;
  patientId: string;
  entries: JournalEntryWithAuthor[];
  appointmentById: Map<string, JournalLinkedAppointment & { id?: string }>;
  courseAppointmentIds: Set<string>;
  courseName: string | null;
  scope: JournalWriteHistoryScope;
  onScopeChange: (scope: JournalWriteHistoryScope) => void;
  practitionerId: string;
  onPractitionerChange: (id: string) => void;
  practitioners: { id: string; name: string }[];
  practitionerMemberIds?: readonly string[];
  activeMemberIds?: readonly string[];
  search: string;
  onSearchChange: (value: string) => void;
  onCite: (html: string) => void;
  onCollapse: () => void;
  onReadFull: (entry: JournalEntryWithAuthor) => void;
  resizable?: boolean;
  width?: number;
  widthMax?: number;
  onWidthChange?: (width: number) => void;
  onWidthReset?: () => void;
  userId?: string | null;
}) {
  const { t, i18n } = useTranslation();
  const tabsId = useId();
  const [openId, setOpenId] = useState<string | null>(null);
  const [panel, setPanel] = useState<JournalHistoryPanel>('notes');
  const [fileCount, setFileCount] = useState<number | null>(null);
  const [markingSearch, setMarkingSearch] = useState('');
  const [markingCount, setMarkingCount] = useState<number | null>(null);
  const bodyRef = useRef<HTMLDivElement | null>(null);

  const visible = useMemo(
    () =>
      filterJournalWriteHistoryEntries(entries, {
        scope,
        courseAppointmentIds,
        practitionerId,
        practitionerIds: new Set(practitionerMemberIds ?? []),
        activeMemberIds: new Set(activeMemberIds ?? []),
        search,
        appointmentById,
      }),
    [
      activeMemberIds,
      appointmentById,
      courseAppointmentIds,
      entries,
      practitionerId,
      practitionerMemberIds,
      scope,
      search,
    ],
  );
  const headerCount =
    panel === 'notes' ? visible.length : panel === 'files' ? fileCount : markingCount;

  const practitionerFilter = (
    <Select
      aria-label={t('journal.filter_practitioner')}
      value={practitionerId}
      onChange={(event) => onPractitionerChange(event.target.value)}
    >
      <option value="">{t('journal.history_practitioners')}</option>
      <option value={JOURNAL_WRITE_ALL_EMPLOYEES}>{t('journal.history_all_employees')}</option>
      {practitioners.map((row) => (
        <option key={row.id} value={row.id}>
          {row.name}
        </option>
      ))}
    </Select>
  );

  const citeOpen = () => {
    const text = selectedTextInNode(bodyRef.current);
    const entry = visible.find((item) => item.id === openId);
    if (!text || !entry) return;
    const when = formatCourseDateTime(entry.encounter_at ?? entry.entry_date, i18n.language);
    const name = entry.author_name || t('journal.untitled');
    onCite(
      journalQuoteHtml({
        text,
        attribution: t('journal.quote_attribution', { date: when, name }),
      }),
    );
  };

  return (
    <aside className="jw-hist">
      <div className="jw-col-head">
        <div className="jw-col-h">
          <div className="jw-hist-title">
            <h2>{t('journal.previous_notes')}</h2>
            {headerCount != null ? (
              <span className="jw-n" data-testid="journal-history-count">
                {headerCount}
              </span>
            ) : null}
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="shrink-0"
            aria-label={t('journal.collapse_history')}
            onClick={onCollapse}
          >
            <ChevronLeft strokeWidth={ICON} />
          </Button>
        </div>
        <JournalHistoryModeTabs idBase={tabsId} value={panel} onChange={setPanel} />
        {panel === 'notes' ? (
          <>
            <Input
              type="search"
              value={search}
              onChange={(event) => onSearchChange(event.target.value)}
              placeholder={t('journal.search_placeholder')}
              aria-label={t('journal.search_placeholder')}
              leadingIcon={<Search className="h-4 w-4" strokeWidth={ICON} aria-hidden />}
            />
            <div className="jw-hist-f">
              {courseAppointmentIds.size > 0 ? (
                <div className="jw-seg" role="group" aria-label={t('journal.history_scope')}>
                  <button
                    type="button"
                    aria-pressed={scope === 'course'}
                    onClick={() => onScopeChange('course')}
                  >
                    {t('journal.this_course')}
                  </button>
                  <button
                    type="button"
                    aria-pressed={scope === 'all'}
                    onClick={() => onScopeChange('all')}
                  >
                    {t('journal.all_notes')}
                  </button>
                </div>
              ) : null}
              {practitionerFilter}
            </div>
            {courseName && scope === 'course' ? (
              <p className="mt-2 text-xs text-ds-muted">{courseName}</p>
            ) : null}
          </>
        ) : panel === 'markings' ? (
          <>
            <Input
              type="search"
              value={markingSearch}
              onChange={(event) => setMarkingSearch(event.target.value)}
              placeholder={t('journal.history_markings_search')}
              aria-label={t('journal.history_markings_search')}
              data-testid="journal-history-markings-search"
              leadingIcon={<Search className="h-4 w-4" strokeWidth={ICON} aria-hidden />}
            />
            <div className="jw-hist-f">{practitionerFilter}</div>
          </>
        ) : null}
      </div>
      {panel === 'notes' ? (
        <ul
          className="jw-list jw-col-body jw-scroller"
          role="tabpanel"
          id={`${tabsId}-notes-panel`}
          aria-labelledby={`${tabsId}-notes`}
        >
          {visible.map((entry) => {
            const appointment = entry.appointment_id
              ? appointmentById.get(entry.appointment_id)
              : undefined;
            const linked = journalDisplayAppointment(entry, appointment);
            const status = journalEntryStatus(entry);
            const kind = journalKindLabel({
              entryType: entry.entry_type,
              serviceName: linked?.service_name,
              typeLabel: (type) => t(`journal.type_${type}`),
            });
            const open = openId === entry.id;
            return (
              <li key={entry.id} className={open ? 'jw-it open' : 'jw-it'}>
                <div className="jw-it-h">
                  <button
                    type="button"
                    className="jw-it-toggle"
                    onClick={() => setOpenId(open ? null : entry.id)}
                  >
                    <span className="m1">
                      {formatCourseDateTime(entry.encounter_at ?? entry.entry_date, i18n.language)}
                    </span>
                    <span className="t">{entry.title || t('journal.untitled')}</span>
                    <span className="s">
                      {[kind, linked?.clinician_name || entry.author_name]
                        .filter(Boolean)
                        .join(' · ')}
                    </span>
                    <span className="pv">{journalNotePreview(entry.content_md)}</span>
                    <span className="m1">
                      <span
                        className={
                          status === 'finalized' ? 'trn' : 'text-xs font-semibold text-ds-amber-ink'
                        }
                      >
                        {t(`journal.filter_status_${status}`)}
                      </span>
                    </span>
                  </button>
                  <Button
                    type="button"
                    variant="ghost"
                    className="jw-read-btn"
                    title={t('journal.read_full_screen')}
                    aria-label={t('journal.read_full_screen')}
                    onClick={() => onReadFull(entry)}
                  >
                    <Maximize2 className="h-4 w-4" strokeWidth={ICON} aria-hidden />
                    {t('journal.read_full_screen')}
                  </Button>
                </div>
                {open ? (
                  <div className="jw-full" ref={bodyRef} onMouseUp={() => undefined}>
                    <JournalFormattedView html={entry.content_html} text={entry.content_md} />
                    <JournalAttachments
                      clinicId={clinicId}
                      patientId={patientId}
                      entryId={entry.id}
                      canEdit={false}
                    />
                    <JournalCorrectionPanel
                      clinicId={clinicId}
                      patientId={patientId}
                      entryId={entry.id}
                      canAmend={false}
                    />
                    <div className="jn-acts mt-3 flex flex-wrap gap-2">
                      <Button type="button" variant="secondary" onClick={citeOpen}>
                        <Quote className="h-4 w-4" strokeWidth={ICON} aria-hidden />
                        {t('journal.cite_selection')}
                      </Button>
                      <p className="w-full text-xs text-ds-muted">{t('journal.cite_hint')}</p>
                    </div>
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      ) : panel === 'files' ? (
        <div
          className="jw-files jw-col-body"
          role="tabpanel"
          id={`${tabsId}-files-panel`}
          aria-labelledby={`${tabsId}-files`}
        >
          <JournalWritePatientFiles
            clinicId={clinicId}
            patientId={patientId}
            userId={userId}
            onVisibleCount={setFileCount}
          />
        </div>
      ) : (
        <div
          className="jw-files jw-col-body"
          role="tabpanel"
          id={`${tabsId}-markings-panel`}
          aria-labelledby={`${tabsId}-markings`}
        >
          <JournalWritePatientMarkings
            clinicId={clinicId}
            patientId={patientId}
            practitionerId={practitionerId}
            practitionerMemberIds={practitionerMemberIds}
            activeMemberIds={activeMemberIds}
            search={markingSearch}
            onVisibleCount={setMarkingCount}
            onCite={onCite}
          />
        </div>
      )}
      {resizable && width != null && widthMax != null && onWidthChange && onWidthReset ? (
        <JournalHistoryResizeHandle
          value={width}
          min={JOURNAL_HISTORY_WIDTH_MIN}
          max={widthMax}
          label={t('journal.resize_history')}
          onChange={onWidthChange}
          onReset={onWidthReset}
        />
      ) : null}
    </aside>
  );
}
